import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, ArrowLeft, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import './PrivacyPage.css';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="privacy-standalone">
      {/* <nav className="privacy-nav">
        <button onClick={() => navigate('/')} className="back-btn-privacy">
          <ArrowLeft size={20} /> Back to Home
        </button>
        <div className="brand-name">🏥 The Doctor At Home</div>
      </nav> */}

      <div className="privacy-wrapper">
        <header className="privacy-hero">
          <h1>Privacy Policy</h1>
          <p className="last-update">Last Updated: February 2026</p>
        </header>

        {/* --- Emergency Disclaimer --- */}
        <section className="emergency-box">
          <ShieldAlert className="alert-icon" size={32} />
          <div>
            <h4>Important: Emergency Disclaimer</h4>
            <p>
              "The Doctor At Home" is NOT for emergencies and does not replace professional medical advice. 
              In a life-threatening situation, call your local emergency number immediately.
            </p>
          </div>
        </section>

        <div className="policy-grid">
          {/* 1. Scope */}
          <section className="policy-block">
            <h3>1. Scope of This Policy</h3>
            <p>
              This Privacy Policy describes how we collect, use, and handle your information when you use our Android mobile application 
              and the related backend services we operate. This policy does not apply to third-party websites or services that may 
              be linked within our application.
            </p>
          </section>

          {/* 2. Information We Collect (Full Detailed Table) */}
          <section className="policy-block">
            <h3>2. Information We Collect</h3>
            <p>We collect only what is necessary to deliver our services:</p>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Examples</th>
                    <th>Purpose</th>
                    <th>Retention</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Account Data</strong></td>
                    <td>Name, phone, email, profile photo, role (patient/doctor)</td>
                    <td>Create/manage account; secure sign-in; personalize experience</td>
                    <td>While account is active or as required by law</td>
                  </tr>
                  <tr>
                    <td><strong>Health & appointment</strong></td>
                    <td>Symptoms/reason, doctor notes, appointment date/time, prescriptions you/doctor upload</td>
                    <td>Enable consultations and maintain history you control</td>
                    <td>Until you delete or request deletion; otherwise per legal obligations</td>
                  </tr>
                  <tr>
                    <td><strong>Location</strong></td>
                    <td>Approximate/precise, foreground/background (if allowed)</td>
                    <td>Match nearby providers, ETA, on-the-way updates</td>
                    <td>Used in real time; not stored continuously; limited time-bound logs only</td>
                  </tr>
                  <tr>
                    <td><strong>Device & app data</strong></td>
                    <td>Device model, OS/app version, identifiers, crash logs</td>
                    <td>Reliability, troubleshooting, security</td>
                    <td>As needed for performance/security analysis</td>
                  </tr>
                  <tr>
                    <td><strong>Media you upload</strong></td>
                    <td>Reports, prescriptions, images/documents</td>
                    <td>Clinical review; bill/invoice generation</td>
                    <td>Until you delete or account deletion, except where law requires longer</td>
                  </tr>
                   <tr>
                    <td><strong>Payments</strong></td>
                    <td>UPI/wallet intent reference, amount, status (no card data stored by us)</td>
                    <td>Process payments, refunds, settlements, invoices</td>
                    <td>As required for audit/tax/legal compliance</td>
                  </tr>
                   <tr>
                    <td><strong>Communications</strong></td>
                    <td>Support tickets, in-app chat/call metadata, push tokens</td>
                    <td>Support; appointment/payment/message notifications</td>
                    <td>For the life of the ticket/need or as required by law</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* 3. App Permissions (Full List) */}
          <section className="policy-block">
            <h3>3. App Permissions and Their Use</h3>
            <p>To function correctly, our app requires the following permissions:</p>
            <div className="permissions-grid">
              <div className="perm-item">
                <CheckCircle2 size={18} />
                <div><strong>Location:</strong>(precise/approximate; may run in background if you allow) – routing, ETA, nearby matches.</div>
              </div>
              <div className="perm-item">
                <CheckCircle2 size={18} />
                <div><strong>Camera & Photos/Media:</strong>capture/upload reports and profile images.</div>
              </div>
              <div className="perm-item">
                <CheckCircle2 size={18} />
                <div><strong>Notifications:</strong>booking, reminders, status updates.</div>
              </div>
              <div className="perm-item">
                <CheckCircle2 size={18} />
                <div><strong>Phone (optional):</strong> place a call to provider/patient; we do not access call logs.</div>
              </div>
              <div className="perm-item">
                <CheckCircle2 size={18} />
                <div><strong>Storage:</strong>generate/download invoices or reports you request.</div>
              </div>
            </div>
          </section>

          {/* 4. How We Use Information */}
          <section className="policy-block">
            <h3>4. How We Use Information</h3>
            <ul className="detailed-list">
              <li>Provide and improve consultations, bookings, home visits, and support.</li>
              <li>Process payments and manage refunds/settlements</li>
              <li>Protect safety, prevent fraud/abuse, and comply with law and Google Play policies.</li>
              <li>Analytics and crash diagnostics to improve stability.</li>
              <li>Send transactional communications you opt into.</li>
            </ul>
          </section>

          {/* 5. Data Sharing and Disclosure */}
          <section className="policy-block">
            <h3>5. Data Sharing and Disclosure</h3>
            <p>We do not sell your personal data. We only share information with:</p>
            <div className="info-box-blue">
              <p><strong>Medical Providers:</strong> Only the assigned doctor/nurse can see your relevant medical info for your visit.</p>
              <p><strong>Cloud Services:</strong> We use secure servers (like AWS/Firebase) to store data with encryption.</p>
              <p><strong>Legal Authorities:</strong> Only when required by law or to protect our rights.</p>
            </div>
          </section>

          {/* 6. Security and Retention */}
          <section className="policy-block">
            <h3>6. Security and Data Retention</h3>
            <div className="security-info">
              <ShieldCheck className="icon-blue" />
              <p>
                We use appropriate technical and organizational measures (transport encryption, access controls, limited retention). No method is 100% secure; we continuously improve protections.
              </p>
            </div>
          </section>

          {/* 7. Children's Privacy */}
          <section className="policy-block">
            <h3>7. Children's Privacy</h3>
            <p>
              Our services are available for use by minors only under the direct supervision of a parent or guardian. 
              We do not knowingly collect data directly from children under 13 without parental consent.
            </p>
          </section>

           <section className="policy-block">
            <h3>8. International transfers</h3>
            <p>
              Data may be processed on servers inside or outside your country. Where required, we implement appropriate safeguards.
            </p>
          </section>

           <section className="policy-block">
            <h3>9.  Retention & deletion</h3>
            <p>
              We retain data only as long as necessary for the purposes described, legal obligations, dispute resolution, and enforcement. You may request account/data deletion; we’ll honor it as required by law and inform you of any data we must retain.
            </p>
          </section>

          {/* 10. Your Rights */}
          <section className="policy-block">
            <h3>10. Your Rights</h3>
            <p>You have the following rights regarding your data:</p>
            <ul className="detailed-list">
              <li>Right to access and export your medical records.</li>
              <li>Right to rectify incorrect information.</li>
              <li>Right to request deletion of your account and data.</li>
              <li>Right to withdraw consent for location or camera tracking.</li>
            </ul>
          </section>

           <section className="policy-block">
            <h3>11.  Changes</h3>
            <p>
              We may update this Policy and will post the new version here with an updated “Last updated” date. Material changes may be notified in-app.
            </p>
          </section>

          {/* 12. Contact Us */}
          <section className="policy-block contact-us">
            <h3>12. Contact Us</h3>
            <p>
              If you have any questions or concerns about this Privacy Policy, please contact our 
              Privacy Officer through the in-app "Help & Support" section or via our official email 
              address listed on the Google Play Store.
            </p>
          </section>
        </div>
      </div>
      
      <footer className="privacy-footer-standalone">
        <p>© 2026 The Doctor At Home. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default PrivacyPage;